import { useCallback, useMemo, useState } from "react";
import type { ShareResultPayload } from "./api";
import { submitShareResultWithRetry } from "./api";
import { decodeShareToken, readShareTokenFromPath } from "./token";
import { useToolUsage } from "../usage/useToolUsage";

export function useShareSession() {
  const token = useMemo(() => readShareTokenFromPath(), []);
  const payload = useMemo(() => (token ? decodeShareToken(token) : null), [token]);
  const startedAt = useMemo(() => Date.now(), []);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const slug = (import.meta.env.VITE_TOOL_SLUG as string | undefined) ?? "";
  useToolUsage({ slug, ready: true, shareToken: token });

  const submitResult = useCallback(
    async (result: Omit<ShareResultPayload, "startedAt">) => {
      if (!token) throw new Error("Geen share-token");
      setSubmitting(true);
      setSubmitError(null);
      try {
        await submitShareResultWithRetry(token, { ...result, startedAt });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Verzenden mislukt";
        setSubmitError(msg);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [token, startedAt],
  );

  return {
    token,
    cfg: payload?.cfg ?? null,
    exp: payload?.exp ?? null,
    startedAt,
    submitResult,
    submitError,
    submitting,
  };
}
